<template>
	<v-card class="elevation-3" color="white">
		<v-card-title class="secondary--text">
			{{ getLabel('findAProfessional', 'Find a Locum') }}
			<v-spacer></v-spacer>
			<v-icon color="secondary" @click="cancelLocumSearch()">mdi-close</v-icon>
		</v-card-title>
		<v-divider />
		<v-card-text>
			<v-form ref="form" v-model="form">
				<p class="font-weight-bold black--text">Type of professional</p>
				<v-select
					v-model="locumProfession"
					color="secondary"
					background-color="#f6f6f6"
					:items="relevantProfessionsV2"
					:rules="[v => !!v || 'Profession is required']"
					filled
					dense
					label="Choose type of professional"
					no-data-text="No more professions to select"
					hide-selected
					required
					:menu-props="{ maxHeight: 304 }"
				></v-select>

				<div v-show="locumProfession">
					<v-row v-if="availableSkills.length">
						<v-col>
							<span class="font-weight-bold black--text" mb-4>Skills (Optional)</span>
							<v-select
								v-model="selectedSkills"
								:items="availableSkills"
								item-text="name"
								item-value="code"
								filled
								dense
								multiple
								chips
								label="Skills & Experience"
							></v-select>
						</v-col>
					</v-row>

					<v-row>
						<v-col class="justify-center text-center">
							<v-btn-toggle v-model="calendarInputMode" mandatory>
								<v-btn
									rounded
									small
									:color="calendarInputMode == 0 ? 'primary' : '#FFF'"
									:class="[calendarInputMode == 0 ? 'white--text' : 'primary--text text--darken-2']"
									style="
										border-radius: 20px 0px 0px 20px;
										font-family: Montserrat;
										font-style: normal;
										font-weight: bold;
										font-size: 14px;
										line-height: 17px;
										text-align: center;
									"
									@click="calculateRequestDates(0)"
								>
									Date range
								</v-btn>

								<v-btn
									rounded
									small
									:color="calendarInputMode == 1 ? 'primary' : '#FFF'"
									:class="[calendarInputMode == 1 ? 'white--text' : 'primary--text text--darken-2']"
									style="
										border-radius: 0px 20px 20px 0px;
										font-family: Montserrat;
										font-style: normal;
										font-weight: bold;
										font-size: 14px;
										line-height: 17px;
										text-align: center;
									"
									@click="calculateRequestDates(1)"
								>
									Specific days
								</v-btn>
							</v-btn-toggle>
						</v-col>
					</v-row>
					<v-date-picker
						v-model="selectedDates"
						:min="new Date().toISOString().substr(0, 10)"
						header-color="primary"
						color="primary"
						full-width
						no-title
						:multiple="calendarInputMode == 1"
						:range="calendarInputMode == 0"
						class="elevation-0"
					></v-date-picker>

					<v-row>
						<v-col>
							<v-dialog
								ref="startDatedialog"
								v-model="startDateModal"
								:value="computedStartDateFormatted"
								clearable
								:return-value.sync="startDate"
								persistent
								width="290px"
							>
								<template #activator="{ on }">
									<span class="font-weight-bold">Start Date:</span>
									<v-text-field v-model="startDate" :rules="[rules.startDate]" filled dense readonly v-on="on"></v-text-field>
								</template>
								<v-date-picker v-model="startDate" :allowed-dates="allowedStartDates" header-color="primary" color="primary" :min="minDate" scrollable>
									<v-spacer></v-spacer>
									<v-btn text color="primary" @click="startDateModal = false">Cancel</v-btn>
									<v-btn
										text
										color="primary"
										@click="
											$refs.startDatedialog.save(startDate);
											if (calendarInputMode === 0) calculateRequestDates();
										"
										>OK</v-btn
									>
								</v-date-picker>
							</v-dialog>
						</v-col>

						<v-col>
							<v-dialog ref="endDateDialog" v-model="endDateModal" :return-value.sync="endDate" persistent width="290px">
								<template #activator="{ on }">
									<span class="font-weight-bold">End Date:</span>
									<v-text-field
										v-model="endDate"
										:value="computedEndDateFormatted"
										clearable
										:rules="[rules.endDate]"
										filled
										dense
										readonly
										v-on="on"
									></v-text-field>
								</template>
								<v-date-picker v-model="endDate" header-color="primary" color="primary" :min="minDate" scrollable>
									<v-spacer></v-spacer>
									<v-btn text color="primary" @click="endDateModal = false">Cancel</v-btn>
									<v-btn
										text
										color="primary"
										@click="
											$refs.endDateDialog.save(endDate);
											if (calendarInputMode === 0) calculateRequestDates();
										"
										>OK</v-btn
									>
								</v-date-picker>
							</v-dialog>
						</v-col>
					</v-row>

					<p class="font-weight-bold black--text mb-0 mt-0">Time (Times can be edited below)</p>
					<v-row>
						<v-col>
							<v-dialog ref="startTimeDialog" v-model="startTimeModal" :return-value.sync="startTime" persistent width="290px">
								<template #activator="{ on }">
									<span class="font-weight-bold">Start Time:</span>
									<v-text-field v-model="startTime" placeholder="Start Time:" readonly filled dense v-on="on"></v-text-field>
								</template>
								<v-time-picker v-if="startTimeModal" v-model="startTime" full-width format="24hr" :allowed-minutes="[0, 15, 30, 45]">
									<v-spacer></v-spacer>
									<v-btn text color="primary" @click="startTimeModal = false">Cancel</v-btn>
									<v-btn
										text
										color="primary"
										@click="
											$refs.startTimeDialog.save(startTime);
											updateRequestDateTimes(startTime, '');
										"
										>OK</v-btn
									>
								</v-time-picker>
							</v-dialog>
						</v-col>

						<v-col>
							<v-dialog ref="endTimedialog" v-model="endTimeModal" :return-value.sync="endTime" persistent width="290px">
								<template #activator="{ on }">
									<span class="font-weight-bold">End Time:</span>
									<v-text-field v-model="endTime" placeholder="End Time:" readonly filled dense v-on="on"></v-text-field>
								</template>
								<v-time-picker v-if="endTimeModal" v-model="endTime" full-width format="24hr" :allowed-minutes="[0, 15, 30, 45]">
									<v-spacer></v-spacer>
									<v-btn text color="primary" @click="endTimeModal = false">Cancel</v-btn>
									<v-btn
										text
										color="primary"
										@click="
											$refs.endTimedialog.save(endTime);
											updateRequestDateTimes('', endTime);
										"
										>OK</v-btn
									>
								</v-time-picker>
							</v-dialog>
						</v-col>
					</v-row>
					<p v-if="startDate !== null && endDate !== null && endDate !== ''" class="font-weight-bold black--text mb-0 mt-0">Change times per day</p>
					<v-row v-if="startDate !== null && endDate !== null && endDate !== ''">
						<v-col>
							<div style="display: flex; overflow: auto">
								<v-card
									v-for="date in request_dates"
									:key="date.date"
									outlined
									elevation="1"
									class="ml-1 mr-1 mt-2 mb-2 pl-1 pr-1"
									style="border-radius: 8px; !important"
									@click="onClickDateIcon(date)"
								>
									<v-row no-gutters>
										<v-col cols="12">
											<calendar-date-icon
												:color="'primary'"
												:date="date.date"
												:start_time="getLocumDayTime(date.date, 'start_time')"
												:end_time="getLocumDayTime(date.date, 'end_time')"
												small
												class="ml-1 mr-1 mt-2 mb-0 justify-center text-center"
												@click="onClickDateIcon(date)"
											>
											</calendar-date-icon>
										</v-col>
									</v-row>
								</v-card>
							</div>
						</v-col>
						<v-col>
							<v-dialog v-model="showEditRequestTimeDialog" persistent scrollable width="290px" :max-width="$vuetify.breakpoint.mobile ? '80%' : '25%'">
								<v-card class="justify-center text-center card-rounded pb-5" min-height="40vh">
									<v-card-title
										class="pa-2 white--text justify-center text-center primary"
										style="font-family: Montserrat; font-style: normal; font-weight: bold; font-size: 12px; line-height: 7px; text-align: center"
									>
										<v-row>
											<v-spacer></v-spacer>
											<span class="headline white--text">{{ EditRequestTimeDialogTitle }}</span>
											<v-spacer></v-spacer>
										</v-row>
										<v-btn icon color="white" @click="closeRequestTimeDialog()">
											<v-icon>mdi-close</v-icon>
										</v-btn>
									</v-card-title>

									<v-card-text class="text-center justify-center">
										<v-container class="card-rounded pb-2 text-center justify-center">
											<v-form>
												<div>
													<v-row>
														<v-col>
															<v-dialog v-model="startTimeModal" :return-value.sync="startTime" persistent width="290px">
																<template #activator="{ on }">
																	<span class="font-weight-bold">Start Time:</span>
																	<v-text-field v-model="startTimeEdit" placeholder="Start Time:" readonly filled dense v-on="on"></v-text-field>
																</template>
																<v-time-picker
																	v-if="startTimeModal"
																	v-model="startTimeChange"
																	full-width
																	format="24hr"
																	:allowed-minutes="[0, 15, 30, 45]"
																>
																	<v-spacer></v-spacer>
																	<v-btn text color="primary" @click="startTimeModal = false">Cancel</v-btn>
																	<v-btn text color="primary" @click="updateStartTimeEdit()">OK</v-btn>
																</v-time-picker>
															</v-dialog>
														</v-col>
													</v-row>
													<v-row>
														<v-col>
															<v-dialog v-model="endTimeModal" :return-value.sync="endTime" persistent width="290px">
																<template #activator="{ on }">
																	<span class="font-weight-bold">End Time:</span>
																	<v-text-field v-model="endTimeEdit" placeholder="End Time:" readonly filled dense v-on="on"></v-text-field>
																</template>
																<v-time-picker
																	v-if="endTimeModal"
																	v-model="endTimeChange"
																	full-width
																	format="24hr"
																	:allowed-minutes="[0, 15, 30, 45]"
																>
																	<v-spacer></v-spacer>
																	<v-btn text color="primary" @click="endTimeModal = false">Cancel</v-btn>
																	<v-btn text color="primary" @click="updateEndTimeEdit()">OK</v-btn>
																</v-time-picker>
															</v-dialog>
														</v-col>
													</v-row>
												</div>
											</v-form>
										</v-container>
									</v-card-text>
									<v-card-actions>
										<v-row class="pb-1">
											<v-spacer />
											<v-col>
												<v-btn block class="white--text mt-1" :height="30" color="primary" @click="saveTimesForDate()"
													><span style="font-size: 10px">Save</span></v-btn
												>
											</v-col>
										</v-row>
									</v-card-actions>
								</v-card>
							</v-dialog>
						</v-col>
					</v-row>
					<p class="font-weight-bold black--text mb-0 mt-0">Budget</p>
					<v-row>
						<v-col>
							<span class="font-weight-bold">From:</span>
							<v-text-field v-model="budgetFrom" :rules="[rules.budgetFrom]" prefix="R" filled dense min="0" step="50" suffix="p/h" type="number"></v-text-field>
						</v-col>
						<v-col>
							<span class="font-weight-bold">To:</span>
							<v-text-field
								v-model="budgetTo"
								:rules="[rules.budgetTo]"
								prefix="R"
								suffix="p/h"
								type="number"
								:min="budgetFrom"
								step="50"
								filled
								dense
								hide-details
								single-line
							></v-text-field>
						</v-col>
					</v-row>

					<div v-if="showLunchOptions">
						<p class="font-weight-bold black--text mb-0 mt-0">Lunch</p>
						<v-row mt-0>
							<v-col class="text-center justify-center">
								<v-spacer />
								<v-btn-toggle v-model="lunchValue" block dense rounded color="secondary" class="white--text" mandatory>
									<v-btn value="paid"> Paid </v-btn>
									<v-btn style="display: none" value="default"></v-btn>
									<v-btn value="unpaid" :disabled="lunchDuration === '0.5'"> Unpaid </v-btn>
								</v-btn-toggle>
								<v-spacer />
							</v-col>
						</v-row>

						<v-row v-if="lunchValue !== 'default'" no-gutters class="mt-3 mb-2">
							<v-col class="text-center justify-center">
								<v-spacer />
								<v-btn-toggle v-model="lunchDuration" mandatory depressed block rounded dense color="secondary" class="white--text">
									<v-btn value="0.5" :disabled="lunchValue === 'unpaid'"> 30 min </v-btn>

									<v-btn value="1"> 1 hour </v-btn>
								</v-btn-toggle>
								<v-spacer />
							</v-col>
						</v-row>
					</div>

					<v-row>
						<v-col>
							<span class="font-weight-bold black--text" mb-4>Years experience</span>
							<v-select
								v-model="years_experience"
								:items="experienceoptions"
								item-text="text"
								item-value="value"
								return-object
								filled
								required
								dense
								label="Experience in Years"
							></v-select>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<span class="font-weight-bold black--text" mb-4>Language</span>
							<v-select
								v-model="locumLanguage"
								:items="languageOptions"
								:rules="[v => !!v || 'Language is required']"
								required
								filled
								dense
								label="Any language"
								no-data-text="No languages to select"
								small-chips
								multiple
								deletable-chips
							></v-select>
						</v-col>
					</v-row>
					<v-row>
						<v-col dense>
							<v-row no-gutters>
								<v-col>
									<v-textarea v-model="notes" label="Notes" background-color="#F4F4F4" rounded outlined no-resize></v-textarea>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</div>
			</v-form>
		</v-card-text>

		<v-card-actions>
			<v-row>
				<v-col>
					<v-btn depressed block rounded class="black--text text-center" @click="cancelLocumSearch()">Cancel</v-btn>
				</v-col>

				<v-col v-if="this.data === 'Public'" dense>
					<v-row class="pa-0" dense>
						<v-spacer />
						<v-btn
							:disabled="(!form || (lunchValue === 'default' && showLunchOptions)) && !offerCreated"
							:loading="apiRequest"
							rounded
							color="orange"
							outlined
							@click="doCreateOffer()"
							>Create offer
							<v-icon right>mdi-arrow-right</v-icon>
						</v-btn>
						<v-spacer />
					</v-row>
				</v-col>

				<v-col v-if="this.data === 'Private'">
					<v-btn
						:disabled="!form || (lunchValue === 'default' && showLunchOptions)"
						:loading="apiRequest"
						depressed
						block
						rounded
						color="primary"
						class="white--text"
						@click="doLocumSearch()"
					>
						Search
						<v-icon right>mdi-arrow-right</v-icon>
					</v-btn>
				</v-col>
			</v-row>
		</v-card-actions>
		<!-- <v-card-actions v-show="desireCreateOffer">
			<v-col dense>
				<v-row class="pa-0" dense>
					<v-spacer />
					<v-btn :loading="apiRequest" rounded color="orange" outlined @click="doCreateOffer()"
						>Submit offer
						<v-icon right>mdi-arrow-right</v-icon>
					</v-btn>
					<v-spacer />
				</v-row>
			</v-col>
		</v-card-actions> -->
	</v-card>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import { bus } from '@/main';

import store from '@/store';
import { getRelevantProfessions } from '@utils';
import { Tracking } from '@/utils';
import CalendarDateIcon from '@/components/custom/CalendarDateIcon.vue';

export default {
	name: 'PracticeLocumSearch',
	components: { CalendarDateIcon },
	props: ['data'],
	data: () => ({
		desireCreateOffer: false,
		user: store.getters.user,
		company: store.getters.company,
		calendarInputMode: 0,
		showEditRequestTimeDialog: false,
		EditRequestTimeDialogTitle: '',
		startTimeEdit: '',
		endTimeEdit: '',
		startTimeChange: '', //variable for dialog to keep separate to not update time incase of cancel
		endTimeChange: '', //variable for dialog to keep separate to not update time incase of cancel
		lunchDuration: null,
		lunchValue: 'default',

		form: false,
		request_dates: [],

		range: [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
		selectedDatesSorted: [],

		calendarpickerdate: [],

		locumProfession: '',

		industriesConfigsObj: store.getters.configs.industries,

		industriesV2: store.getters.configs.industriesV2,

		budgetFrom: null,
		budgetTo: null,

		years_experience: { text: 'Any Experience', value: 0 },
		experienceoptions: [
			{ text: 'Any Experience', value: 0 },
			{ text: '1 Years +', value: 1 },
			{ text: '2 Years +', value: 2 },
			{ text: '3 Years +', value: 3 },
			{ text: '4 Years +', value: 4 },
			{ text: '5 Years +', value: 5 },
			{ text: '6 Years +', value: 6 },
			{ text: '7 Years +', value: 7 },
			{ text: '8 Years +', value: 8 },
			{ text: '9 Years +', value: 9 },
			{ text: '10 Years +', value: 10 },
			{ text: '11 Years +', value: 11 },
			{ text: '12 Years +', value: 12 },
		],
		locumLanguage: ['English'],
		languageOptions: store.getters.configs.languages,

		selectedSkills: [],

		API_URL: store.getters.getAPI,

		startDateModal: false,
		endDateModal: false,

		startTime: '09:00',
		endTime: '17:00',

		startDate: null,
		endDate: null,
		minDate: new Date().toISOString().substr(0, 10),

		startTimeModal: false,
		endTimeModal: false,

		notes: '',

		apiRequest: false,

		rules: {
			startDate: v => !!v || 'Start date required',
			endDate: v => !!v || 'End date required',
			budgetFrom: v => !!v || 'Required',
			budgetTo: v => !!v || 'Required',
		},
		offerCreated: false,
		filteredPartialResults: [],
		unfilteredPartialResults: [],

		exactMatchesToDisplay: [],
		allExactMatches: [],
		industryKeys: store.getters.configs.industryKeys,
	}),

	computed: {
		...mapGetters('offers', ['offer']),
		selectedDates: {
			get: function () {
				return this.selectedDatesSorted;
			},
			set: function (newValue) {
				this.selectedDatesSorted = newValue.sort((a, b) => moment(a).diff(b, 'days'));
			},
		},

		lunchPeriod() {
			if (!this.hasLunchBreak) {
				return 0;
			}
			return this.lunchDuration === '1' ? 1 : 0.5;
		},
		hasLunchBreak() {
			return this.showLunchOptions && this.lunchValue !== 'default';
		},
		hasPaidLunch() {
			return this.hasLunchBreak && this.lunchValue === 'paid';
		},

		lengthOfDay() {
			return this.formatTime(this.endTime).diff(this.formatTime(this.startTime), 'hours');
		},

		showLunchOptions() {
			return this.lengthOfDay > 5;
		},

		computedStartDateFormatted() {
			return this.startDate ? moment(this.startDate).format('dddd, MMMM Do YYYY') : '';
		},

		computedEndDateFormatted() {
			return this.endDate ? moment(this.endDate).format('dddd, MMMM Do YYYY') : '';
		},

		relevantProfessions() {
			const { industry } = this.company;
			let professions = getRelevantProfessions(industry, this.industriesConfigsObj);
			let nursingProfessions = this.industriesConfigsObj['Nursing'];
			if (professions.includes(nursingProfessions[0]) && !professions[0].header) {
				let len = nursingProfessions.length;
				professions.splice(len, 0, { header: industry });
				professions.splice(len, 0, { divider: true });
				professions.unshift({ header: 'Nursing' });
			} else {
				if (!professions[0]?.header) {
					professions.unshift({ header: industry });
				}
			}
			return professions;
		},

		relevantProfessionsV2() {
			//TODO: Refactor this. Currently implemented to match functionality of old relevantProfessions
			const industryObj = {};

			for (const [key, value] of Object.entries(this.industriesV2)) {
				industryObj[key] = value.professions.map(x => x.name);
			}

			const { industry } = this.company;
			let professions = getRelevantProfessions(industry, industryObj);
			let nursingProfessions = industryObj['Nursing'] || [];
			if (professions.includes(nursingProfessions[0]) && !professions[0].header) {
				let len = nursingProfessions.length;
				professions.splice(len, 0, { header: industry });
				professions.splice(len, 0, { divider: true });
				professions.unshift({ header: 'Nursing' });
			} else {
				if (!professions[0]?.header) {
					professions.unshift({ header: industry });
				}
			}
			return professions;
		},

		industryObjectV2() {
			const { industry } = this.company || {};

			if (industry && this.locumProfession && this.industriesV2) {
				const result = this.industriesV2[industry]?.professions.find(({ name }) => name === this.locumProfession);
				return result;
			}
			return {};
		},

		availableSkills() {
			if (!this.locumProfession) return [];

			if (this.industryObjectV2?.skills) {
				const skills = this.industryObjectV2.skills;
				let flattenedSkills = [];
				for (let skill of skills) {
					if (skill.options?.length) {
						for (let option of skill.options) {
							flattenedSkills.push({
								code: option.code,
								name: `${skill.name} - ${option.name}`,
							});
						}
					} else {
						flattenedSkills.push(skill);
					}
				}

				return flattenedSkills;
			} else {
				return [];
			}
		},

		getPracticeOffer() {
			return store.getters.getPracticeOffer;
		},

		getRequestDates() {
			const { request_dates } = this.offer; //available_dates, start_time, end_time,
			let available_dates = request_dates;
			return available_dates;
		},
	},

	watch: {
		selectedDatesSorted() {
			this.startDate = this.selectedDatesSorted[0];
			this.endDate = this.selectedDatesSorted[this.selectedDatesSorted.length - 1];
			this.calculateRequestDates();
		},
	},
	methods: {
		...mapActions(['SET_PRACTICE_LOCUMS_APPLIED_TO']),
		...mapActions('offers', ['UpsertOffer']),
		...mapMutations('offers', ['SET_PRACTICE_OFFER']),

		formatTime(val) {
			return moment(moment().format('YYYY-MM-DD ') + val);
		},
		allowedEndDates(val) {
			return this.startDate ? moment(val).isSameOrAfter(moment(this.startDate)) : true;
		},
		allowedStartDates(val) {
			return this.endDate ? moment(val).isSameOrBefore(moment(this.endDate)) : true;
		},

		allowedDatesAll(val) {
			return this.allowedEndDates(val) && this.allowedStartDates(val);
		},

		toggleLunch() {
			this.hasLunchBreak = !this.hasLunchBreak;
		},

		toggleLunch30() {
			this.hashourlunch = false;
			this.hashalfhourlunch = !this.hashalfhourlunch;
		},

		toggleLunch60() {
			this.hashalfhourlunch = false;
			this.hashourlunch = !this.hashourlunch;
		},

		cancelLocumSearch() {
			bus.$emit('change-context', 'practice-dashboard-landing-zone');
		},
		async doLocumSearch() {
			this.apiRequest = true;

			const company = store.getters.company;
			const user = store.getters.user;

			let dates = [];
			if (this.calendarInputMode == 1) {
				dates = this.selectedDatesSorted;
			} else {
				let startDay = this.selectedDatesSorted[0];
				let endDay = this.selectedDatesSorted[this.selectedDatesSorted.length - 1];
				const countDays = startDay === endDay ? 0 : moment(endDay).startOf('day').diff(moment(startDay).endOf('day'), 'days') + 1;

				for (let day_index = 0; day_index <= countDays; day_index++) {
					let day = moment(startDay).add(day_index, 'days');
					dates.push(day.format('YYYY-MM-DD'));
				}
			}
			const body = {
				start_time: moment(this.request_dates[0].start_time).format('HH:mm'),
				end_time: moment(this.request_dates[this.request_dates.length - 1].end_time).format('HH:mm'),
				date: this.startDate,
				end_date: this.endDate,
				company: {
					id: company.id,
					name: company.name,
					contact: company.contact,
					email: company.email,
					industry: user.industry,
					address: company.address,
				},
				rates: [parseInt(this.budgetFrom), parseInt(this.budgetTo)],
				notes: this.notes,
				profession: this.locumProfession,
				experience: this.years_experience.value,
				languages: this.locumLanguage,
				skills: this.selectedSkills,
				lunch: {
					allowed: this.hasLunchBreak,
					period: this.lunchPeriod,
					paid: this.hasPaidLunch,
				},
				available_dates: dates,
				request_dates: this.request_dates,
			};
			Tracking.track('Practice_LocumSearch_Search_Click', {
				start_time: body.start_time,
				end_time: body.end_time,
				date: this.startDate,
				end_date: this.endDate,
				rates: [parseInt(this.budgetFrom), parseInt(this.budgetTo)],
				profession: this.locumProfession,
				experience: this.years_experience,
				languages: this.locumLanguage,
				searchDetails: {
					available_dates: dates,
					lunch: {
						allowed: this.hasLunchBreak,
						period: this.lunchPeriod,
						paid: this.hasPaidLunch,
					},
				},
			});

			this.apiRequest = true;

			this.SET_PRACTICE_LOCUMS_APPLIED_TO();

			await store.dispatch('PERFORM_LOCUM_SEARCH', {
				profession: this.locumProfession,
				segments: [[this.startDate, this.endDate]],
				languages: this.locumLanguage,
				location: `${company.address.province},${company.address.region}`,
				rates: [parseInt(this.budgetFrom), parseInt(this.budgetTo)],
				experience: this.years_experience.value,
				skills: this.selectedSkills,
				dates,
			});
			this.apiRequest = false;

			this.SET_PRACTICE_OFFER(body);
			if (this.data === 'Private') bus.$emit('change-context', 'practice-locum-search-results', this.data);
		},

		async search() {
			this.apiRequest = true;

			const company = store.getters.company;
			const user = store.getters.user;

			let dates = [];
			if (this.calendarInputMode == 1) {
				dates = this.selectedDatesSorted;
			} else {
				let startDay = this.selectedDatesSorted[0];
				let endDay = this.selectedDatesSorted[this.selectedDatesSorted.length - 1];
				const countDays = startDay === endDay ? 0 : moment(endDay).startOf('day').diff(moment(startDay).endOf('day'), 'days') + 1;

				for (let day_index = 0; day_index <= countDays; day_index++) {
					let day = moment(startDay).add(day_index, 'days');
					dates.push(day.format('YYYY-MM-DD'));
				}
			}
			const body = {
				start_time: moment(this.request_dates[0].start_time).format('HH:mm'),
				end_time: moment(this.request_dates[this.request_dates.length - 1].end_time).format('HH:mm'),
				date: this.startDate,
				end_date: this.endDate,
				company: {
					id: company.id,
					name: company.name,
					contact: company.contact,
					email: company.email,
					industry: user.industry,
					address: company.address,
				},
				rates: [parseInt(this.budgetFrom), parseInt(this.budgetTo)],
				notes: this.notes,
				profession: this.locumProfession,
				experience: this.years_experience.value,
				languages: this.locumLanguage,
				skills: this.selectedSkills,
				lunch: {
					allowed: this.hasLunchBreak,
					period: this.lunchPeriod,
					paid: this.hasPaidLunch,
				},
				available_dates: dates,
				request_dates: this.request_dates,
			};

			this.apiRequest = true;

			this.SET_PRACTICE_LOCUMS_APPLIED_TO();

			await store.dispatch('PERFORM_LOCUM_SEARCH', {
				profession: this.locumProfession,
				segments: [[this.startDate, this.endDate]],
				languages: this.locumLanguage,
				location: `${company.address.province},${company.address.region}`,
				rates: [parseInt(this.budgetFrom), parseInt(this.budgetTo)],
				experience: this.years_experience.value,
				skills: this.selectedSkills,
				dates,
			});
			this.apiRequest = false;

			this.SET_PRACTICE_OFFER(body);
			//if (this.data === 'Private') bus.$emit('change-context', 'practice-locum-search-results', this.data);
		},

		calculateRequestDates(inputmode = null) {
			if (this.startDate === null && this.endDate === null) return;
			if (inputmode !== null) {
				switch (inputmode) {
					case 0:
						this.calendarInputMode = 0;
						break;
					case 1:
						this.calendarInputMode = 1;
						break;

					default:
						break;
				}
			}
			this.request_dates = [];
			if (this.calendarInputMode == 1) {
				for (let index = 0; index < this.selectedDatesSorted.length; index++) {
					let date = moment(this.selectedDatesSorted[index]).format('YYYY-MM-DD');
					this.request_dates.push({
						date: date,
						end_time: moment(`${date} ${this.endTime}`).format(),
						start_time: moment(`${date} ${this.startTime}`).format(),
					});
				}
			} else {
				let startDay = this.startDate; //this.selectedDatesSorted[0];
				let endDay = this.endDate; //this.selectedDatesSorted[this.selectedDatesSorted.length - 1];
				const countDays = startDay === endDay ? 0 : moment(endDay).startOf('day').diff(moment(startDay).endOf('day'), 'days') + 1;

				for (let day_index = 0; day_index <= countDays; day_index++) {
					let day = moment(startDay).add(day_index, 'days').format('YYYY-MM-DD');
					this.request_dates.push({
						date: day,
						end_time: moment(`${day} ${this.endTime}`).format(),
						start_time: moment(`${day} ${this.startTime}`).format(),
					});
				}
			}
			return this.request_dates;
		},

		getLocumDayTime(day, time) {
			if (this.request_dates) {
				const request_date = this.request_dates.find(({ date }) => date === day)[time];
				return moment(request_date).format('HH:mm');
			}

			return '';
		},

		onClickDateIcon(date) {
			this.EditRequestTimeDialogTitle = date.date;
			this.startTimeEdit = moment(date.start_time).format('HH:mm');
			this.endTimeEdit = moment(date.end_time).format('HH:mm');
			this.startTimeChange = this.startTimeEdit;
			this.endTimeChange = this.endTimeEdit;
			this.showEditRequestTimeDialog = true;
		},

		closeRequestTimeDialog() {
			this.showEditRequestTimeDialog = false;
		},

		updateStartTimeEdit() {
			this.startTimeEdit = this.startTimeChange;
			this.startTimeModal = false;
		},

		updateEndTimeEdit() {
			this.endTimeEdit = this.endTimeChange;
			this.endTimeModal = false;
		},

		saveTimesForDate() {
			let newDateTime = {
				date: this.EditRequestTimeDialogTitle,
				end_time: moment(`${this.EditRequestTimeDialogTitle} ${this.endTimeEdit}`).format(),
				start_time: moment(`${this.EditRequestTimeDialogTitle} ${this.startTimeEdit}`).format(),
			};
			var index = this.request_dates.findIndex(date => date.date == this.EditRequestTimeDialogTitle);
			this.request_dates[index] = newDateTime;
			this.showEditRequestTimeDialog = false;
		},

		updateRequestDateTimes(startTime, endTime) {
			if (startTime !== '') {
				for (let index = 0; index < this.request_dates.length; index++) {
					this.request_dates[index].start_time = moment(`${this.request_dates[index].date} ${startTime}`).format();
				}
			} else if (endTime !== '') {
				for (let index = 0; index < this.request_dates.length; index++) {
					this.request_dates[index].end_time = moment(`${this.request_dates[index].date} ${endTime}`).format();
				}
			}
		},

		getMappedSkills(skills) {
			return skills.map(code => ({ code, name: this.industryKeys[code] }));
		},

		// setUnfilteredPartialResults(partialResults) {
		// 	if (partialResults === []) return;
		// 	partialResults.Items.filter(locum => !(locum.id in partialResults)).forEach(locum => {
		// 		let copy = { ...locum };
		// 		const available_dates = [];
		// 		const booking_requests = [];
		// 		const { date, end_date, start_time, end_time } = this.getPracticeOffer;

		// 		copy = { ...copy, available_dates, booking_requests, date, end_date, start_time, end_time };
		// 		copy.professional = locum;

		// 		if (locum.approved) this.unfilteredPartialResults.push(copy);
		// 	});
		// },

		async doCreateOffer() {
			this.apiRequest = true;
			//await this.doLocumSearch();
			await this.search();
			//Get matches
			this.allExactMatches = store.getters.getPracticeLocumSearchResults;
			if (this.allExactMatches !== []) this.exactMatchesToDisplay = this.allExactMatches.slice(0, 5);

			let partialResults = store.getters.getLocumBroadSearch;
			//this.setUnfilteredPartialResults(partialResults);
			if (this.unfilteredPartialResults !== []) this.filteredPartialResults = this.unfilteredPartialResults.slice(0, 5);

			var IDsToMail = [];
			this.exactMatchesToDisplay.forEach(exactMatch => {
				IDsToMail.push(exactMatch.id);
			});

			partialResults.Items.forEach(partialMatch => {
				IDsToMail.push(partialMatch.id);
			});
			let payload = this.offer;
			delete payload['update_timestamp'];
			delete payload.company.address['update_timestamp'];

			payload.request_dates = this.getRequestDates;

			payload.notes = this.notes;

			payload.skills = this.getMappedSkills(payload.skills);

			payload.IDsToMail = IDsToMail;

			try {
				await this.UpsertOffer(payload);
			} finally {
				this.apiRequest = false;
				this.offerCreated = true;
				setTimeout(() => {
					bus.$emit('UpdateCalendar');
				}, 500);
				store.commit('PUSH_NOTIFICATION', { component: 'practice-offer-set', data: ['derpderp'] });
				bus.$emit('change-context', 'practice-dashboard-landing-zone');
			}
		},

		desiresCreateOffer() {
			this.desireCreateOffer = true;
		},
	},
};
</script>
